import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Grid,
  Skeleton,
  useMediaQuery,
  useTheme,
  Divider,
  LinearProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { useSelector } from "react-redux";

const apiService = {
  getQualificationRules: async (baseUrl) => {
    const url = `${baseUrl}crm/qualification-rules/`;
    const res = await customGetAPI(url);
    return res.ok ? res.responseDetails : [];
  },

  createQualificationRule: async (baseUrl, data) => {
    const url = `${baseUrl}crm/qualification-rules/`;
    const res = await customAPI(url, "POST", data);
    return res.responseDetails;
  },

  updateQualificationRule: async (baseUrl, id, data) => {
    const url = `${baseUrl}crm/qualification-rules/${id}/`;
    const res = await customAPI(url, "PUT", data);
    return res.responseDetails;
  },

  deleteQualificationRule: async (baseUrl, id) => {
    const url = `${baseUrl}crm/qualification-rules/${id}/`;
    const res = await customAPI(url, "DELETE", {});
    return res.responseDetails;
  },

  startScoreRecalculation: async (baseUrl) => {
    const url = `${baseUrl}crm/score-recalculation/start_recalculation/`;
    const res = await customAPI(url, "POST", {});
    return res.responseDetails;
  },

  getTaskStatus: async (baseUrl, taskId) => {
    const url = `${baseUrl}crm/score-recalculation/task_status/?task_id=${taskId}`;
    const res = await customGetAPI(url);
    return res.responseDetails;
  },

  getRecentTasks: async (baseUrl) => {
    const url = `${baseUrl}crm/score-recalculation/recent_tasks/`;
    const res = await customGetAPI(url);
    return res.responseDetails;
  },

  getCustomFieldDefinitions: async (baseUrl) => {
    const url = `${baseUrl}crm/custom-field-definitions/`;
    const res = await customGetAPI(url);
    return res.responseDetails;
  },

  getContactFields: async (baseUrl) => {
    const url = `${baseUrl}crm/contact-fields/`;
    const res = await customGetAPI(url);
    return res.responseDetails || [];
  },
};

const useScoreRecalculationWebSocket = (taskId) => {
  const ws = useRef(null);
  const { domain } = useSelector((state) => state.tokenReducer);
  const [progressData, setProgressData] = useState(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 3;

  useEffect(() => {
    if (!taskId) {
      setProgressData(null);
      return;
    }

    if (ws.current) {
      ws.current.close();
      ws.current = null;
    }

    const connectWebSocket = () => {
      try {
        const wsUrl = `wss://${window.location.host
          .replace("http://", "")
          .replace("https://", "")}/ws/score-recalculation/?task_id=${taskId}`;
        ws.current = new WebSocket(wsUrl);

        ws.current.onopen = () => {
          console.log("WebSocket connected for task:", taskId);
          reconnectAttempts.current = 0; // Reset reconnect attempts on successful connection
        };

        ws.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (
              data.type === "score_progress" ||
              data.type === "current_status"
            ) {
              setProgressData({
                progress: data.progress,
                status: data.status,
                message: data.data?.message,
                updatedCount: data.data?.updated_count,
              });
            }
          } catch (error) {
            console.error("Error parsing WebSocket message:", error);
          }
        };

        ws.current.onclose = (event) => {
          console.log(
            "WebSocket disconnected for task:",
            taskId,
            "Code:",
            event.code
          );

          // Only attempt reconnect if it wasn't a normal closure and we have attempts left
          if (
            event.code !== 1000 &&
            reconnectAttempts.current < maxReconnectAttempts
          ) {
            reconnectAttempts.current += 1;
            console.log(
              `Attempting reconnect ${reconnectAttempts.current}/${maxReconnectAttempts}`
            );
            setTimeout(connectWebSocket, 2000 * reconnectAttempts.current); // Exponential backoff
          }
        };

        ws.current.onerror = (error) => {
          console.error("WebSocket error:", error);
        };
      } catch (error) {
        console.error("Error creating WebSocket:", error);
      }
    };

    connectWebSocket();

    return () => {
      if (ws.current) {
        ws.current.close(1000, "Component unmounting"); // Normal closure
        ws.current = null;
      }
    };
  }, [taskId, domain]);

  return progressData;
};

const usePersistentTask = () => {
  const [activeTask, setActiveTask] = useState(null);

  // Load active task from localStorage on mount
  useEffect(() => {
    const savedTask = localStorage.getItem("active_score_recalculation_task");
    if (savedTask) {
      try {
        const taskData = JSON.parse(savedTask);
        // Only set as active if it's not completed and not too old (less than 1 hour)
        const isRecent =
          taskData.updatedAt &&
          Date.now() - new Date(taskData.updatedAt).getTime() < 60 * 60 * 1000;

        if (
          (taskData.status === "PROGRESS" || taskData.status === "PENDING") &&
          isRecent
        ) {
          setActiveTask(taskData);
        } else {
          // Clear old or completed tasks
          localStorage.removeItem("active_score_recalculation_task");
        }
      } catch (error) {
        console.error("Error loading saved task:", error);
        localStorage.removeItem("active_score_recalculation_task");
      }
    }
  }, []);

  const setTask = useCallback((taskData) => {
    const taskWithTimestamp = {
      ...taskData,
      updatedAt: new Date().toISOString(),
    };
    setActiveTask(taskWithTimestamp);
    localStorage.setItem(
      "active_score_recalculation_task",
      JSON.stringify(taskWithTimestamp)
    );
  }, []);

  const clearTask = useCallback(() => {
    setActiveTask(null);
    localStorage.removeItem("active_score_recalculation_task");
  }, []);

  return { activeTask, setTask, clearTask };
};

const operators = [
  { value: "equals", label: "Equals" },
  { value: "not_equals", label: "Not Equals" },
  { value: "contains", label: "Contains (comma-separated)" },
  { value: "does_not_contain", label: "Does Not Contain (comma-separated)" },
  { value: "is_set", label: "Is Not Empty" },
  { value: "is_not_set", label: "Is Empty" },
  { value: "greater_than", label: "Greater Than (for numbers)" },
  { value: "less_than", label: "Less Than (for numbers)" },
];

const TableSkeleton = () => (
  <TableContainer component={Paper}>
    <Table>
      <TableHead>
        <TableRow>
          {[...Array(6)].map((_, index) => (
            <TableCell key={index}>
              <Skeleton variant="text" height={30} />
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {[...Array(5)].map((_, rowIndex) => (
          <TableRow key={rowIndex}>
            {[...Array(6)].map((_, cellIndex) => (
              <TableCell key={cellIndex}>
                <Skeleton variant="text" height={25} />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const CardSkeleton = () => (
  <Card sx={{ mb: 3 }}>
    <CardHeader title={<Skeleton variant="text" height={40} width={300} />} />
    <CardContent>
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <Skeleton variant="rectangular" width={200} height={56} />
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" width={150} height={36} />
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" width={200} height={36} />
        </Grid>
      </Grid>
    </CardContent>
  </Card>
);

export default function LeadQualificationManager() {
  const { domain } = useSelector((state) => state.tokenReducer);
  const [rules, setRules] = useState([]);
  const [threshold, setThreshold] = useState(100);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [contactFields, setContactFields] = useState([]);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const { activeTask, setTask, clearTask } = usePersistentTask();
  const [taskProgress, setTaskProgress] = useState({
    progress: 0,
    status: "IDLE",
    message: "",
    updatedCount: 0,
  });

  const isVisibleRef = useRef(true);

  const statusCheckIntervalRef = useRef(null);
  const activeTaskRef = useRef(activeTask);

  useEffect(() => {
    activeTaskRef.current = activeTask;
  }, [activeTask]);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const progressData = useScoreRecalculationWebSocket(activeTask?.task_id);

  const checkTaskStatus = useCallback(async () => {
    const currentActiveTask = activeTaskRef.current;

    if (!currentActiveTask?.task_id || !isVisibleRef.current) {
      return;
    }

    try {
      const currentStatus = await apiService.getTaskStatus(
        domain,
        currentActiveTask.task_id
      );
      console.log("Periodic task status check:", currentStatus);

      if (
        currentStatus.status === "SUCCESS" ||
        currentStatus.status === "FAILURE"
      ) {
        // Task is completed, update our state
        setTaskProgress({
          progress: currentStatus.progress,
          status: currentStatus.status,
          message: currentStatus.result?.message || "",
          updatedCount: currentStatus.result?.updated_count || 0,
        });

        // Update persistent task
        setTask({
          ...currentActiveTask,
          progress: currentStatus.progress,
          status: currentStatus.status,
          message: currentStatus.result?.message || "",
          updatedCount: currentStatus.result?.updated_count || 0,
        });

        // Clear the interval since task is done
        if (statusCheckIntervalRef.current) {
          clearInterval(statusCheckIntervalRef.current);
          statusCheckIntervalRef.current = null;
        }

        // Auto-clear after delay
        setTimeout(() => {
          clearTask();
          setTaskProgress({
            progress: 0,
            status: "IDLE",
            message: "",
            updatedCount: 0,
          });
        }, 3000);
      }
    } catch (error) {
      console.error("Error checking task status:", error);
      if (statusCheckIntervalRef.current) {
        clearInterval(statusCheckIntervalRef.current);
        statusCheckIntervalRef.current = null;
      }
      clearTask();
    }
  }, [domain, setTask, clearTask]);

  useEffect(() => {
    const taskId = activeTask?.task_id;
    const taskStatus = activeTask?.status;

    if (taskId && (taskStatus === "PROGRESS" || taskStatus === "PENDING")) {
      console.log("Setting up status check interval for task:", taskId);

      // Clear any existing interval
      if (statusCheckIntervalRef.current) {
        clearInterval(statusCheckIntervalRef.current);
        statusCheckIntervalRef.current = null;
      }

      // Set up new interval - check every 30 seconds (reduced frequency)
      statusCheckIntervalRef.current = setInterval(checkTaskStatus, 30000);

      // Also check immediately (but with a small delay to avoid immediate calls)
      const immediateCheckTimer = setTimeout(() => {
        checkTaskStatus();
      }, 2000);

      // Cleanup on unmount or when task changes
      return () => {
        if (statusCheckIntervalRef.current) {
          clearInterval(statusCheckIntervalRef.current);
          statusCheckIntervalRef.current = null;
        }
        clearTimeout(immediateCheckTimer);
      };
    } else {
      // Clear interval if no active task or task is completed
      if (statusCheckIntervalRef.current) {
        clearInterval(statusCheckIntervalRef.current);
        statusCheckIntervalRef.current = null;
      }
    }
  }, [activeTask?.task_id, activeTask?.status, checkTaskStatus]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;

      // If page becomes visible and we have an active task, check status immediately
      if (isVisibleRef.current && activeTask?.task_id) {
        // Small delay to avoid immediate API call on every tab switch
        const visibilityTimer = setTimeout(() => {
          checkTaskStatus();
        }, 1000);

        return () => clearTimeout(visibilityTimer);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [activeTask?.task_id, checkTaskStatus]);

  const showToast = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // FIXED: Update task progress when WebSocket data changes - NO API CALLS
  useEffect(() => {
    if (progressData) {
      console.log("WebSocket progress update:", progressData);
      setTaskProgress(progressData);

      // Update persistent task
      if (activeTask) {
        setTask({
          ...activeTask,
          ...progressData,
        });
      }

      // If WebSocket sends completion, clear any status check interval
      if (
        progressData.status === "SUCCESS" ||
        progressData.status === "FAILURE"
      ) {
        if (statusCheckIntervalRef.current) {
          clearInterval(statusCheckIntervalRef.current);
          statusCheckIntervalRef.current = null;
        }

        const timer = setTimeout(() => {
          clearTask();
          setTaskProgress({
            progress: 0,
            status: "IDLE",
            message: "",
            updatedCount: 0,
          });

          showToast(
            progressData.status === "SUCCESS"
              ? `Recalculation completed! Updated ${
                  progressData.updatedCount || 0
                } contacts.`
              : "Recalculation failed!",
            progressData.status === "SUCCESS" ? "success" : "error"
          );
        }, 3000);

        return () => clearTimeout(timer);
      }
    }
  }, [progressData, activeTask, setTask, clearTask, showToast]);

  // Initialize task progress from active task
  useEffect(() => {
    if (activeTask) {
      setTaskProgress({
        progress: activeTask.progress || 0,
        status: activeTask.status || "PENDING",
        message: activeTask.message || "",
        updatedCount: activeTask.updatedCount || 0,
      });

      // If we have an active task on mount, verify its status immediately
      // But only if it's not already completed
      if (
        activeTask.task_id &&
        (activeTask.status === "PROGRESS" || activeTask.status === "PENDING")
      ) {
        // Longer delay to ensure everything is settled
        const verifyTimer = setTimeout(() => {
          checkTaskStatus();
        }, 3000);

        return () => clearTimeout(verifyTimer);
      }
    } else {
      setTaskProgress({
        progress: 0,
        status: "IDLE",
        message: "",
        updatedCount: 0,
      });
    }
  }, []);

  // Standard contact fields
  const standardContactFields = useRef([
    { value: "first_name", label: "First Name" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone" },
    { value: "company", label: "Company" },
    { value: "position", label: "Position" },
    { value: "employee_count", label: "Employee Count" },
    { value: "industry_type", label: "Industry Type" },
    { value: "notes", label: "Notes" },
  ]);

  const loadData = useCallback(
    async (checkTaskStatus = false) => {
      setIsLoading(true);
      try {
        const [rulesData, customFieldDefs, contactFieldsData] =
          await Promise.all([
            apiService.getQualificationRules(domain),
            apiService.getCustomFieldDefinitions(domain),
            apiService
              .getContactFields(domain)
              .catch(() => standardContactFields.current),
          ]);

        setRules(rulesData || []);
        setContactFields(
          Array.isArray(contactFieldsData)
            ? contactFieldsData
            : standardContactFields.current
        );

        const customFieldOptions = (customFieldDefs || []).map((def) => ({
          value: `custom_fields.${def.field_name}`,
          label: def.field_label || `Custom: ${def.field_name}`,
          fieldType: def.field_type,
        }));
        setCustomFieldDefinitions(customFieldOptions);

        if (checkTaskStatus && activeTask?.task_id) {
          try {
            const currentStatus = await apiService.getTaskStatus(
              domain,
              activeTask.task_id
            );
            console.log("Initial task status check:", currentStatus);

            if (
              currentStatus.status === "PROGRESS" ||
              currentStatus.status === "PENDING"
            ) {
              setTaskProgress({
                progress: currentStatus.progress,
                status: currentStatus.status,
                message: currentStatus.result?.message || "",
                updatedCount: currentStatus.result?.updated_count || 0,
              });
            } else {
              clearTask();
            }
          } catch (error) {
            console.error("Error checking task status:", error);
            clearTask();
          }
        }
      } catch (error) {
        console.error("Error loading qualification settings:", error);
        showToast("Failed to load settings.", "error");
        setContactFields(standardContactFields.current);
      }
      setIsLoading(false);
    },
    [domain, clearTask, showToast]
  );

  useEffect(() => {
    loadData(true);
  }, []); // Empty dependency array - load only once

  const handleSaveThreshold = async () => {
    if (threshold === "" || isNaN(threshold)) {
      showToast("Please enter a valid threshold number.", "error");
      return;
    }
    showToast("Threshold feature coming soon!", "info");
  };

  const handleOpenDialog = (rule = null) => {
    setEditingRule(
      rule
        ? { ...rule }
        : {
            field: "position",
            operator: "contains",
            value: "",
            points: 10,
            is_active: true,
            is_mandatory: false,
          }
    );
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingRule(null);
  };

  const handleSaveRule = async () => {
    if (!editingRule?.field) {
      showToast("Please select a field.", "error");
      return;
    }

    if (!editingRule?.operator) {
      showToast("Please select a condition.", "error");
      return;
    }

    if (editingRule.points === "" || isNaN(editingRule.points)) {
      showToast("Please enter valid points.", "error");
      return;
    }

    if (
      !["is_set", "is_not_set"].includes(editingRule.operator) &&
      !editingRule.value?.trim()
    ) {
      showToast("Please enter a value to check against.", "error");
      return;
    }

    setIsSaving(true);
    try {
      const dataToSave = {
        ...editingRule,
        points: parseInt(editingRule.points),
        is_active: editingRule.is_active ?? true,
        is_mandatory: editingRule.is_mandatory ?? false,
      };

      let response;
      if (editingRule.id) {
        response = await apiService.updateQualificationRule(
          domain,
          editingRule.id,
          dataToSave
        );
        showToast("Rule updated successfully!");
      } else {
        response = await apiService.createQualificationRule(domain, dataToSave);
        showToast("Rule created successfully!");
      }

      // If response includes task_id, start tracking it
      if (response.task_id) {
        setTask({
          task_id: response.task_id,
          progress: 0,
          status: "PENDING",
          message: "Starting score recalculation...",
        });

        setTaskProgress({
          progress: 0,
          status: "PENDING",
          message: "Starting score recalculation...",
          updatedCount: 0,
        });

        showToast("Score recalculation started in background!", "info");
      }

      handleCloseDialog();
      await loadData();
    } catch (error) {
      console.error("Error saving rule:", error);
      showToast("Failed to save rule.", "error");
    }
    setIsSaving(false);
  };

  const handleDeleteRule = async (ruleId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this rule? All lead scores will be recalculated."
      )
    ) {
      return;
    }

    try {
      const response = await apiService.deleteQualificationRule(domain, ruleId);
      showToast("Rule deleted successfully!");

      if (response.task_id) {
        setTask({
          task_id: response.task_id,
          progress: 0,
          status: "PENDING",
          message: "Starting score recalculation...",
        });

        setTaskProgress({
          progress: 0,
          status: "PENDING",
          message: "Starting score recalculation...",
          updatedCount: 0,
        });

        showToast("Score recalculation started in background!", "info");
      }

      await loadData();
    } catch (error) {
      console.error("Error deleting rule:", error);
      showToast("Failed to delete rule.", "error");
    }
  };

  // Start recalculation task
  const startRecalculationTask = useCallback(async () => {
    try {
      const response = await apiService.startScoreRecalculation(domain);

      setTask({
        task_id: response.task_id,
        progress: 0,
        status: "PENDING",
        message: "Starting score recalculation...",
      });

      setTaskProgress({
        progress: 0,
        status: "PENDING",
        message: "Starting score recalculation...",
        updatedCount: 0,
      });

      showToast("Score recalculation started!", "info");
    } catch (error) {
      console.error("Error starting score recalculation:", error);
      showToast("Failed to start score recalculation.", "error");
    }
  }, [domain, setTask, showToast]);

  // Manual recalculation
  const recalculateAllScores = useCallback(async () => {
    await startRecalculationTask();
  }, [startRecalculationTask]);

  // Determine if recalculation is in progress
  const isRecalculating =
    taskProgress.status === "PROGRESS" || taskProgress.status === "PENDING";
  const isCompleted =
    taskProgress.status === "SUCCESS" || taskProgress.status === "FAILURE";

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <CardSkeleton />
        <Card>
          <CardHeader
            title={<Skeleton variant="text" height={40} width={200} />}
          />
          <CardContent>
            <TableSkeleton />
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Snackbar for notifications */}
      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ mb: 2 }}
        >
          {snackbar.message}
        </Alert>
      )}

      {/* Optional Threshold Card */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Qualification Score Threshold" />
        <CardContent>
          <Grid
            container
            spacing={2}
            alignItems="center"
            flexWrap={{ xs: "wrap", sm: "nowrap" }}
          >
            <Grid item xs={12} sm="auto">
              <TextField
                type="number"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                label="Threshold Score"
                variant="outlined"
                sx={{ width: { xs: "100%", sm: 200 } }}
              />
            </Grid>
            <Grid item xs={12} sm="auto">
              <Button
                variant="contained"
                onClick={handleSaveThreshold}
                disabled={isSaving}
                startIcon={
                  isSaving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Save Threshold
              </Button>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
                <Button
                  variant="outlined"
                  onClick={recalculateAllScores}
                  disabled={isRecalculating}
                  startIcon={
                    isRecalculating ? (
                      <CircularProgress size={20} />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    mb: isRecalculating || isCompleted ? 1 : 0,
                  }}
                >
                  {isRecalculating
                    ? `Recalculating... ${taskProgress.progress}%`
                    : isCompleted
                    ? "Recalculation Completed"
                    : "Recalculate All Scores"}
                </Button>

                {/* Progress Bar */}
                {isRecalculating && (
                  <Box sx={{ width: "100%" }}>
                    <LinearProgress
                      variant="determinate"
                      value={taskProgress.progress}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                    {taskProgress.message && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {taskProgress.message}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Completion Message */}
                {isCompleted && (
                  <Typography
                    variant="caption"
                    color={
                      taskProgress.status === "SUCCESS"
                        ? "success.main"
                        : "error.main"
                    }
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {taskProgress.message ||
                      (taskProgress.status === "SUCCESS"
                        ? "Recalculation completed successfully!"
                        : "Recalculation failed!")}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Rest of your component remains the same */}
      <Card>
        <CardHeader
          title={`Scoring Rules (${rules.length})`}
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                width: { xs: "100%", sm: "auto" },
                backgroundColor: "#84DEB8",
                color: "#000",
                "&:hover": { backgroundColor: "#6cc9a1" },
              }}
            >
              Add Rule
            </Button>
          }
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: { xs: 2, sm: 0 },
          }}
        />
        <CardContent>
          {rules.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="textSecondary" gutterBottom>
                No qualification rules defined yet.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => handleOpenDialog()}
                sx={{ mt: 1 }}
              >
                Create First Rule
              </Button>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                maxWidth: "100%",
                overflowX: "auto",
              }}
            >
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Field</TableCell>
                    <TableCell>Condition</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell>Points</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rules.map((rule) => {
                    const allFields = [
                      ...contactFields,
                      ...customFieldDefinitions,
                    ];
                    const fieldLabel =
                      allFields.find((f) => f.value === rule.field)?.label ||
                      rule.field;
                    const operatorLabel = operators.find(
                      (o) => o.value === rule.operator
                    )?.label;

                    return (
                      <TableRow key={rule.id}>
                        <TableCell>
                          <Box
                            display="flex"
                            alignItems="center"
                            flexWrap="wrap"
                          >
                            <Typography variant="body2" fontWeight="medium">
                              {fieldLabel}
                            </Typography>
                            {customFieldDefinitions.some(
                              (cf) => cf.value === `custom_fields.${rule.field}`
                            ) && (
                              <Chip
                                label="Custom"
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ ml: 1, mt: { xs: 0.5, sm: 0 } }}
                              />
                            )}

                            {rule.is_mandatory && (
                              <Chip
                                label="Mandatory"
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ ml: 1, mt: { xs: 0.5, sm: 0 } }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {operatorLabel}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            component="code"
                            sx={{
                              bgcolor: "grey.100",
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: "0.75rem",
                              wordBreak: "break-word",
                              display: "inline-block",
                              maxWidth: "200px",
                            }}
                          >
                            "{rule.value}"
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{ color: "success.main", fontWeight: "medium" }}
                        >
                          +{rule.points}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={rule.is_active ? "Active" : "Inactive"}
                            color={rule.is_active ? "success" : "default"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(rule)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteRule(rule.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Rule Dialog - Matches Image Design */}
      <Dialog
        open={showDialog}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleCloseDialog();
          }
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        disableEscapeKeyDown={false}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" component="h2">
            {editingRule?.id ? "Edit" : "Create"} Qualification Rule
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Contact Field */}
            <Box>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Contact Field
              </Typography>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Select Field</InputLabel>
                <Select
                  value={editingRule?.field || ""}
                  label="Select Field"
                  onChange={(e) =>
                    setEditingRule({ ...editingRule, field: e.target.value })
                  }
                >
                  <MenuItem value="standard-header" disabled>
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      sx={{ fontWeight: "bold" }}
                    >
                      Standard Fields
                    </Typography>
                  </MenuItem>
                  {contactFields.map((field) => (
                    <MenuItem key={field.value} value={field.value}>
                      {field.label}
                    </MenuItem>
                  ))}
                  {customFieldDefinitions.length > 0 && (
                    <>
                      <MenuItem value="divider" disabled>
                        <Divider />
                      </MenuItem>
                      <MenuItem value="custom-header" disabled>
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          sx={{ fontWeight: "bold" }}
                        >
                          Custom Fields
                        </Typography>
                      </MenuItem>
                    </>
                  )}
                  {customFieldDefinitions.map((field) => (
                    <MenuItem key={field.value} value={field.value}>
                      {field.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Condition */}
            <Box>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Condition
              </Typography>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Select Condition</InputLabel>
                <Select
                  value={editingRule?.operator || ""}
                  label="Select Condition"
                  onChange={(e) =>
                    setEditingRule({ ...editingRule, operator: e.target.value })
                  }
                >
                  {operators.map((operator) => (
                    <MenuItem key={operator.value} value={operator.value}>
                      {operator.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Value to Check Against */}
            <Box>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Value to Check Against
              </Typography>
              <TextField
                placeholder="e.g., CEO, Manager, Technology (comma-separated for multiple)"
                value={editingRule?.value || ""}
                onChange={(e) =>
                  setEditingRule({ ...editingRule, value: e.target.value })
                }
                disabled={["is_set", "is_not_set"].includes(
                  editingRule?.operator
                )}
                fullWidth
                variant="outlined"
                multiline
                rows={2}
              />
              {["contains", "does_not_contain"].includes(
                editingRule?.operator
              ) && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  You can provide multiple values separated by commas (e.g.,
                  "Pricing,Budget,Interested"). The condition will be met if
                  "any" of the provided values match.
                </Typography>
              )}
            </Box>

            {/* Points to Award */}
            <Box>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Points to Award
              </Typography>
              <TextField
                type="number"
                placeholder="e.g., 10"
                value={editingRule?.points || ""}
                onChange={(e) =>
                  setEditingRule({ ...editingRule, points: e.target.value })
                }
                fullWidth
                variant="outlined"
                inputProps={{ min: -100, max: 100 }}
              />
            </Box>

            {/* Mandatory Field Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={editingRule?.is_mandatory || false}
                  onChange={(e) =>
                    setEditingRule({
                      ...editingRule,
                      is_mandatory: e.target.checked,
                    })
                  }
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Mark as Mandatory Field
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    When scoring, leads will be flagged if this field is empty.
                    This does not affect score, only flags missing data.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: "flex-start", mt: 1 }}
            />
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveRule}
            variant="contained"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} /> : null}
            sx={{
              minWidth: 140,
              backgroundColor: "#84DEB8",
              color: "#000",
              "&:hover": { backgroundColor: "#6cc9a1" },
            }}
          >
            {isSaving ? "Saving..." : editingRule?.id ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
